import { useFormikContext, getIn } from 'formik';
import { useState, useCallback } from 'react';
import { debounce, throttle } from 'throttle-debounce';

/**
 * local constants
 */ const DEFAULT_FIELD_THROTTLE_MS = 800;
/**
 * Helper to retrieve a Field value from Formik form values.
 * Handles case of nested properties in key - i.e. `primarykey.subkey.key`).
 */ const getFieldValueByKey = (values, key)=>{
    // Keys may be of the form 'primarykey.subkey.key' to access
    // nested value properties.
    const value = getIn(values, key);
    return value;
};
/**
 * Wraps a Fields base element `onChange` handler so that
 * side-effects can be performed when setting Formik Field values.
 *
 * @param setValue - Formik setValue field helper
 * @param onFieldChange - base Field change handler
 */ const onChangeFieldWrapper = (onFieldChange, setValue)=>(value)=>{
        setValue(value);
        if (!!onFieldChange) {
            onFieldChange(value);
        }
    };
/**
 * Custom hook which wraps a (Text) Field base element `onChange` & `onBlur` handlers
 * so that side-effects can be performed when setting Formik Field values.
 *
 * Also adds debounce to default formik change handler methods. This stops the entire
 * Formik re-rendering on every keystroke
 *
 * @param fieldName - the field name
 * @param onFieldChange - base Field change handler
 * @param onFieldBlur - base Field blur handler
 * @param throttleMS - if > 50, will throttle and debounce the Field change/blur handlers
 * along with Formik value updates
 */ const useTextFieldWrappers = (fieldName, onFieldChange = ()=>{}, onFieldBlur = ()=>{}, throttleMS = 0)=>{
    const { handleChange , handleBlur , values  } = useFormikContext();
    const [focused, setFocused] = useState(false);
    const [value, setValue] = useState(undefined);
    // determine whether we should throttle
    const isThrottledField = typeof throttleMS === "number" && throttleMS > 50;
    const debouncedHandleChange = useCallback(isThrottledField ? debounce(throttleMS, handleChange) : handleChange, [
        isThrottledField,
        throttleMS
    ]);
    const throttledOnFieldChange = useCallback(isThrottledField ? throttle(throttleMS, false, !!onFieldChange ? onFieldChange : ()=>{}) : onFieldChange, [
        onFieldChange,
        isThrottledField,
        throttleMS
    ]);
    const onChange = useCallback((e)=>{
        e.persist();
        setValue(e.target.value);
        debouncedHandleChange(e);
        if (!!onFieldChange) {
            throttledOnFieldChange(e.target.value);
        }
    }, [
        debouncedHandleChange,
        onFieldChange,
        throttledOnFieldChange
    ]);
    const onFocus = useCallback((e)=>{
        setFocused(true);
        setValue(values[fieldName]);
    }, [
        values,
        fieldName
    ]);
    const onBlur = useCallback((e)=>{
        const trimmedValue = e.target.value.trim();
        e.persist();
        setFocused(true);
        setValue(trimmedValue);
        handleBlur(e);
        if (!!onFieldBlur) {
            onFieldBlur(trimmedValue);
        }
    }, [
        handleBlur,
        onFieldBlur
    ]);
    return {
        onChange,
        onBlur,
        onFocus,
        value: focused && isThrottledField ? value : getFieldValueByKey(values, fieldName)
    };
};
/**
 * Whether field has an error
 *
 * @deprecated - see `inputs/FieldCommon`
 */ const hasError = (fieldMeta)=>{
    return fieldMeta.touched && !!fieldMeta.error;
};

export { DEFAULT_FIELD_THROTTLE_MS, getFieldValueByKey, hasError, onChangeFieldWrapper, useTextFieldWrappers };
